import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { deploymentsApi, type BacpacFile } from '@/services/deployments'

export interface AzureSizePrice {
  sku: string
  // OS the price was resolved for (the pricing source prices one OS per size config)
  os_type?: string
  retail_price: number
  unit_price: number
  currency: string
  monthly_estimate: number
  product_name: string
  error?: string
}

export type AzurePricingResult = Record<string, AzureSizePrice>

export interface RossDisksBreakdown {
  disks: Array<{ gb: number; tier: string; monthly: number }>
  count: number
  total_monthly: number
}

export interface RossDeploymentCost {
  region: string
  vm_sku: string
  total_vms: number
  app_server_count: number
  breakdown: {
    vms: { count: number; per_vm_monthly: number; total_monthly: number }
    os_disks: { count: number; per_disk_monthly: number; total_monthly: number }
    data_disks: RossDisksBreakdown
    public_ips: { count: number; per_ip_monthly: number; total_monthly: number }
    load_balancer: { monthly: number } | null
    app_gateway: { monthly: number } | null
  }
  total_monthly: number
}

export interface UseRossDeploymentCostParams {
  region: string
  size: string
  appServerCount: number
  dataDiskSizes: number[]
}

export function useRossDeploymentCost(params: UseRossDeploymentCostParams | null) {
  return useQuery({
    queryKey: ['ross-deployment-cost', params],
    queryFn: async () => {
      if (!params) return null
      const res = await api.get<RossDeploymentCost>('/products/ross-deployment-cost/', {
        params: {
          region: params.region,
          size: params.size,
          app_server_count: params.appServerCount,
          data_disk_sizes: params.dataDiskSizes.join(','),
        },
      })
      return res.data
    },
    enabled: !!params && !!params.region && !!params.size,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  })
}

export function useAzurePricing(region: string, productSlug?: string) {
  return useQuery({
    queryKey: ['azure-pricing', region, productSlug ?? 'default'],
    queryFn: async () => {
      const res = await api.get<AzurePricingResult>('/products/azure-pricing/', {
        params: { region, ...(productSlug ? { product_slug: productSlug } : {}) },
      })
      return res.data
    },
    enabled: !!region,
    staleTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  })
}

export { type BacpacFile }

export function useBacpacFiles(environment = '') {
  const queryClient = useQueryClient()
  // Key by environment so prod and non-prod lists don't share a cache entry —
  // switching Environment in the wizard re-fetches from the right storage account.
  const queryKey = ['bacpac-files', environment]
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await deploymentsApi.getBacpacFiles(false, environment)
      return res.data
    },
    staleTime: 60 * 60 * 1000, // 1 hour — backend caches for the same duration
    retry: 1,
  })

  const refresh = () => {
    // fetchQuery with staleTime:0 bypasses the TanStack cache and passes ?refresh=true to bust backend cache
    queryClient.fetchQuery({
      queryKey,
      queryFn: async () => {
        const res = await deploymentsApi.getBacpacFiles(true, environment)
        return res.data
      },
      staleTime: 0,
    })
  }

  return { ...query, refresh }
}
